import { useMemo } from 'react';
import { css } from '@emotion/react';
import {
  NoShowRateByType,
  RoiType,
} from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import { Virtuoso } from 'react-virtuoso';
import { ChartCTA, DemoChip, InfoTipPopover } from '@frontend/analytics-shared';
import { Chart } from '@frontend/charts';
import { EmptyStates } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { formatters } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Avatar, Text, Truncated } from '@frontend/design-system';
import { useFetchNSRROITimeline } from '../hooks/use-fetch-nsr-roi-timeline';
import { useNSRRoiShallowStore } from '../hooks/use-nsr-roi-store';

interface Props {
  isDemoAccount?: boolean;
}

export const NSRRoiComparisonByProviderChart = ({ isDemoAccount }: Props) => {
  const { t } = useTranslation('analytics');
  const { filters } = useNSRRoiShallowStore('filters');

  const { isLoading, rawData } = useFetchNSRROITimeline({
    additionalRequestPayload: {
      roiType: RoiType.WEAVE,
      noShowRateType: NoShowRateByType.APPOINTMENTS_PRACTITIONER,
    },
    filters,
  });

  const { highlight, description, sortedProviders } = useMemo(() => {
    const providers = rawData?.data?.aggregates
      ? [...rawData.data.aggregates].sort((a, b) => (b.value || 0) - (a.value || 0))
      : [];

    return {
      sortedProviders: providers,
      highlight: providers.length ? providers[0].name || '' : t('NA'),
      description: providers.length
        ? t('Reduce no-shows by sending targeted appointment reminders filtered by provider')
        : t('No appointment data available to determine provider no-show rates'),
    };
  }, [rawData, t]);

  return (
    <Chart css={styles.chart} isLoading={isLoading}>
      <Chart.Header
        title={t('No-Show Rate by Provider')}
        infoTip={<InfoTipPopover>{t('Analyze no-show trends across providers')}</InfoTipPopover>}
        leftElement={isDemoAccount && <DemoChip />}
        removeHeaderBottomMargin
      />
      {sortedProviders?.length ? (
        <div css={styles.listWrapper}>
          <Virtuoso
            computeItemKey={(index, item) => item.name ?? index}
            data={sortedProviders}
            itemContent={(_index, { name, value }) => {
              return <RowItem key={name} name={name} value={value} />;
            }}
            totalCount={sortedProviders.length}
          />
        </div>
      ) : (
        <EmptyStates
          emptyStateConfig={{
            header: '',
            styles: styles.emptyState,
            type: 'analytics',
          }}
        />
      )}
      <ChartCTA
        buttonLabel={t('Send Reminder')}
        ctaType='sendReminders'
        title={t('Highest No-Show')}
        highlight={highlight}
        description={description}
      />
    </Chart>
  );
};

interface RowItemProps {
  name?: string;
  value?: number;
}

const RowItem = ({ name, value }: RowItemProps) => {
  if (!name) return null;

  return (
    <div css={styles.item}>
      <div css={styles.avatarWrapper}>
        <Avatar disableClick name={name} />
        <Truncated className='person-name'>{name}</Truncated>
      </div>
      <Text className='value'>{formatters.appendPercent(value)}</Text>
    </div>
  );
};

const styles = {
  emptyState: css`
    height: 220px;
    justify-content: center;
    padding-top: ${theme.spacing(1.25)};
    padding: 0;

    > div:last-of-type {
      display: none;
    }
  `,

  listWrapper: css`
    background-color: ${theme.colors.neutral5};
    border-radius: ${theme.borderRadius.medium};
    flex: 1;
    min-height: 320px;
    padding: ${theme.spacing(2, 0, 2, 2)};
  `,

  item: css`
    align-items: center;
    background-color: ${theme.colors.white};
    border-radius: ${theme.borderRadius.medium};
    border: 1px solid ${theme.colors.neutral20};
    display: flex;
    gap: ${theme.spacing(2)};
    justify-content: space-between;
    margin-bottom: ${theme.spacing(1)};
    margin-right: ${theme.spacing(2)};
    padding: ${theme.spacing(1.5)};

    .person-name {
      font-size: ${theme.font.size.medium};
    }

    .value {
      font-weight: ${theme.font.weight.bold};
      font-size: ${theme.fontSize(20)};
    }
  `,

  avatarWrapper: css`
    align-items: center;
    display: flex;
    gap: ${theme.spacing(1)};
    max-width: calc(100% - ${theme.spacing(8)});
  `,

  chart: css`
    .chart-content {
      justify-content: space-around;
    }
  `,
};
